import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Microphone, SpeakerHigh, Pause, CaretRight, Fire } from "@phosphor-icons/react";
import api, { fileUrl } from "../lib/api";

/**
 * ReactionChainsStrip — horizontally scrolling row of posts with the most
 * voice reactions (viral chains). Shown on Explore. Auto-hides when empty.
 */
export default function ReactionChainsStrip() {
  const [chains, setChains] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/posts/reaction-chains/trending", { params: { limit: 10 } })
      .then(({ data }) => setChains(data || []))
      .catch(() => {});
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, []);

  const togglePlay = (e, post) => {
    e.stopPropagation();
    if (playingId === post.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(fileUrl(post.voice_drop_path));
    a.onended = () => setPlayingId(null);
    a.play().catch(() => {});
    audioRef.current = a;
    setPlayingId(post.id);
  };

  if (chains.length === 0) return null;

  return (
    <section className="px-5 mt-5" data-testid="reaction-chains-strip">
      <p className="text-sky-300 text-xs tracking-[0.2em] uppercase font-bold mb-3 flex items-center gap-1.5">
        <Fire size={14} weight="fill" /> Reaction Chains
      </p>
      <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1">
        {chains.map((p) => (
          <div
            key={p.id}
            onClick={() => nav(`/p/${p.id}`)}
            className="flex-shrink-0 snap-start w-[210px] rounded-2xl crystal-glass px-3 py-2.5 hover:border-sky-400/40 transition-colors cursor-pointer"
            data-testid={`chain-card-${p.id}`}
          >
            <div className="flex items-center gap-2">
              {p.voice_drop_path ? (
                <button
                  onClick={(e) => togglePlay(e, p)}
                  data-testid={`chain-play-${p.id}`}
                  className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    playingId === p.id ? "bg-sky-400 text-ink animate-pulse" : "bg-sky-400/20 text-sky-300"
                  }`}
                >
                  {playingId === p.id ? <Pause size={16} weight="fill" /> : <SpeakerHigh size={16} weight="fill" />}
                </button>
              ) : (
                <div className="w-9 h-9 rounded-full bg-sky-400/20 text-sky-300 flex items-center justify-center flex-shrink-0">
                  <Microphone size={16} weight="fill" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-cream font-bold text-xs truncate leading-tight">@{p.author?.username || "dancer"}</p>
                <p className="text-cream/50 text-[10px] truncate">{p.caption || "Voice drop"}</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-sky-300 text-[9px] tracking-[0.2em] uppercase font-black flex items-center gap-1">
                <Microphone size={10} weight="fill" /> {p.voice_reactions_count} reactions
              </span>
              <CaretRight size={10} className="text-cream/40" weight="bold" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
